import _ from "lodash";

export const REPORT_TYPES = {
  STANDARD: "STANDARD",
  CUSTOM: "CUSTOM",
  SUPERSET: "SUPERSET"
};

export const REPORT_SECTION_DETAILS = [
  {
    type: REPORT_TYPES.STANDARD,
    name: "Standard reports",
    description: "Pre-defined reports offering insights into key metrics."
  },
  {
    type: REPORT_TYPES.CUSTOM,
    name: "Custom reports",
    description: "Flexible reports tailored to specific needs, providing detailed and specialized insights."
  }
];

export const FILE_TYPES = {
  CSV: "csv",
  PDF: "pdf",
  PNG: "png"
};

export const SUB_REPORT_TYPES = {
  CHART: "chart",
  TABLE: "table",
  CARD: "card"
};

export const CHART_TYPES = {
  LINE: "line",
  BAR: "bar",
  STACKED_BAR: "stack_bar",
  PIE: "pie",
  MAP: "map",
  BUBBLE: "bubble"
};

export const CUSTOM_CARD_TYPES = {
  COMPLIANCE_GAUGE: "compliance_gauge",
  COMPLIANCE_GROUP_SCORE: "compliance_group_score",
  SUMMARY_KPIS: "summary_kpis"
};

export const CUSTOM_TABLE_TYPES = {
  HEATMAP: "heatmap",
  NEW_TABLE: "new_table"
};

export const CUSTOM_FILTER_DATE_RANGE = {
  FIRST: "first",
  SECOND: "second"
};

export const SUB_REPORT_KEYS = {
  OVERALL_DATA_COLLECTION_WITH_COMPLETION_METRICS: "overall_data_collection_with_completion_metrics"
};

export const DEFAULT_CHART_HEIGHT = "350";
export const MAXIMIZE_CHART_HEIGHT = "500";

export const AXIS_TYPES = {
  CATEGORY: "category",
  DATETIME: "datetime"
};

export const DEFAULT_CHART_COLORS = ["#589ddb", "#afd3ee", "#33b7e0", "#7375c8", "#33b986", "#98fcc9", "#68b3ff", "#68b3ff", "#ffaec8", "#477ff2"];

const DEFAULT_MAX_ROW_PER_LABEL = 2;

export const DEFAULT_THRESHOLD_FRONTEND_DOWNLOAD = 500;

function convertToMultiLinesCategories(categories, chartWidth, visiblePoints) {
  const availableWidthPerCategory = chartWidth / visiblePoints;
  const estimatedCharsPerLine = Math.floor(availableWidthPerCategory / 25);
  const nestedCategories = [];

  const flatCategories = categories.map(item => (Array.isArray(item) ? item.join(" ") : item));
  flatCategories.flat().forEach(category => {
    const words = category.split(" ");
    let currentLine = [];
    let currentLength = 0;
    let line = "";

    words.forEach((word, index) => {
      const isLastWord = index === words.length - 1;

      if (currentLine.length + 1 === DEFAULT_MAX_ROW_PER_LABEL) {
        line = line ? `${line} ${word}` : word;
        if (isLastWord) {
          currentLine.push(line);
        }
        return;
      }

      if (currentLength + word.length + 1 >= estimatedCharsPerLine) {
        if (line) {
          currentLine.push(line);
        }
        line = word;
      } else {
        line = line ? `${line} ${word}` : word;
      }

      if (isLastWord) {
        currentLine.push(line);
      }

      currentLength = line.length;
    });
    nestedCategories.push(currentLine);
  });

  return nestedCategories;
}

let isUpdating = false;

const updateCategories = chartContext => {
  if (!chartContext.w.globals.dom.baseEl) {
    isUpdating = false;
    return;
  }
  const chartWidth = chartContext.w.globals.dom.baseEl.offsetWidth;
  const visiblePoints = chartContext.w.globals.dataPoints;
  const originalCategories = chartContext.w.globals.categoryLabels;

  const newCategories = convertToMultiLinesCategories(originalCategories, chartWidth, visiblePoints);
  chartContext
    .updateOptions({
      xaxis: {
        categories: newCategories
      }
    })
    .then(() => {
      isUpdating = false;
    });
};

const updateCategoriesDebounced = _.debounce(updateCategories, 500);

export const DEFAULT_CHART_OPTIONS = {
  chart: {
    events: {
      mounted: function(chartContext) {
        updateCategoriesDebounced(chartContext);
      },
      updated: function(chartContext) {
        // Check if the mouse is within the chart's bounding box
        const chartElement = chartContext.w.globals.dom.baseEl;
        const mouseOverChart = chartElement.matches(":hover");

        // Prevent update if hovering over the chart area
        if (mouseOverChart) {
          return;
        }
        if (isUpdating) {
          return;
        }

        isUpdating = true;
        updateCategoriesDebounced(chartContext);
      }
    },
    parentHeightOffset: 0,
    height: 350,
    zoom: {
      enabled: true,
      type: "x"
    },
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 0,
      tools: {
        download: false,
        selection: false,
        zoom: false,
        pan: false,
        zoomin: '<img src="/static/img/icon-zoomin.svg" width="35">',
        zoomout: '<img src="/static/img/icon-zoomout.svg" width="35">',
        reset: false,
        customIcons: []
      },
      autoSelected: "zoom"
    }
  },
  dataLabels: {
    enabled: false
  },
  grid: {
    padding: {
      right: 30
    },
    xaxis: {
      lines: {
        show: false
      }
    },
    yaxis: {
      lines: {
        show: false
      }
    }
  },
  xaxis: {
    categories: [],
    type: "category",
    tickPlacement: "on",
    title: {
      style: {
        color: "#959fa4",
        fontSize: "9px",
        fontFamily: "RobotoRegular",
        letterSpacing: "0.5px",
        fontWeight: "500",
        cssClass: "apex-title-with-margin"
      }
    },
    labels: {
      trim: true,
      show: true,
      showDuplicates: false,
      hideOverlappingLabels: false,
      rotate: 0,
      maxHeight: 80,
      style: {
        fontSize: "10px",
        colors: "#b0bac9",
        fontFamily: "Roboto-Regular",
        fontWeight: 400,
        cssClass: "to-uppercase-text apexcharts-x-axis-labels"
      }
    }
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    tickAmount: 6,
    axisBorder: {
      show: true,
      offsetX: -2.0
    },
    labels: {
      show: true,
      style: {
        colors: "#b0bac9",
        fontSize: "9px",
        fontFamily: "Roboto-Regular",
        fontWeight: 400,
        cssClass: "apexcharts-xaxis-title"
      }
    },
    title: {
      text: "",
      rotate: -90,
      style: {
        color: "#959fa4",
        fontSize: "9px",
        fontFamily: "RobotoRegular",
        letterSpacing: "0.5px",
        fontWeight: "500",
        cssClass: "apexcharts-xaxis-title"
      }
    }
  },
  fill: {
    type: "gradient",
    gradient: {
      shade: "light",
      type: "horizontal",
      shadeIntensity: 0.5,
      gradientToColors: ["#317bb8", "#97bfde", "#199cc0", "#5357ac", "#096", "#1dcc91", "#96c", "#96caff", "#ff83ac", "#1853c9"], // Gradients for each series
      inverseColors: true,
      opacityFrom: 1,
      opacityTo: 1,
      stops: [0, 100]
    }
  },
  legend: {
    position: "bottom",
    offsetY: 10,
    markers: {
      shape: "circle"
    }
  }
};

export const DEFAULT_BAR_CHART_OPTIONS = _.merge(DEFAULT_CHART_OPTIONS, {});

export const DEFAULT_LINE_CHART_OPTIONS = _.merge(DEFAULT_CHART_OPTIONS, {
  markers: {
    width: 2.5,
    size: 5,
    strokeWidth: 2,
    hover: {
      size: 7
    }
  },
  colors: ["#7375c8", "#33b986", "#98fcc9", "#b389e6", "#68b3ff", "#33b7e0", "#afd3ee"]
});

export const DEFAULT_PIE_CHART_OPTIONS = _.merge(DEFAULT_CHART_OPTIONS, {});

export const DATE_TIME_TOOLTIP = {
  tooltip: {
    shared: true,
    intersect: false,
    custom: function({ series, seriesIndex, dataPointIndex, w }) {
      const label = w.globals.categoryLabels[dataPointIndex];
      const stringLabel = Array.isArray(label) ? label.join(" ") : label;

      let tooltipContent = `
        <div class="custom-tooltip" style="background-color: white; color: black; padding: 10px; border-radius: 5px; border: 1px solid #ccc;">
          <strong>${stringLabel}</strong><br>
      `;

      series.forEach((value, index) => {
        const legendName = w.config.series[index].name;
        const tooltipValue = value[dataPointIndex];
        if (!legendName || (!tooltipValue && tooltipValue !== 0)) {
          return;
        }
        const color = w.config.colors[index];

        tooltipContent += `
          <div style="display: flex; align-items: center; margin: 5px 0;">
            <div style="width: 10px; height: 10px; background-color: ${color}; margin-right: 5px;"></div>
            <strong>${legendName}:</strong>&nbsp;${tooltipValue}
          </div>
        `;
      });

      tooltipContent += "</div>";
      return tooltipContent;
    }
  }
};

const renderTooltipLine = (text, color) =>
  `<div style="display: flex; align-items: center; margin: 5px 0;">
    <div style="width: 10px; height: 10px; background-color: ${color}; margin-right: 5px;"></div>
    ${text}
  </div>`;

const buildTooltipContentByTemplate = (template, color, value, data) => {
  let tooltipContent = "";
  let str = template;
  const variableRegex = /\$\{([^}]+)\}/g; // extract variable from syntax "${var}"
  const matches = [];
  let match;
  while ((match = variableRegex.exec(str)) !== null) {
    matches.push(match[1]);
  }

  for (const v of matches) {
    const replacementRegex = new RegExp(String.raw`\$\{${v}\}`, "g");
    if (v === "value") {
      str = str.replace(replacementRegex, value);
    } else if (v.startsWith("_.")) {
      str = str.replace(replacementRegex, data[v.replace("_.", "")]);
    }
  }

  const lines = str.split("<br/>");
  for (let index = 0; index < lines.length; index++) {
    const renderedColor = index === 0 ? color : "transparent";
    tooltipContent += renderTooltipLine(lines[index], renderedColor);
  }
  return tooltipContent;
};

export const CATEGORY_TOOLTIP = {
  tooltip: {
    custom: function({ series, seriesIndex, dataPointIndex, w }) {
      const value = series[seriesIndex][dataPointIndex];
      const tooltipConfig = w.config.customMetadata.tooltipConfig;
      const dataPoints = w.config.customMetadata.dataPoints || [];

      const label = w.config.plotOptions.bar.horizontal ? w.globals.labels[dataPointIndex] : w.globals.categoryLabels[dataPointIndex];
      const stringLabel = Array.isArray(label) ? label.join(" ") : label;
      const color = w.config.colors[seriesIndex];

      let tooltipContent = `
        <div class="custom-tooltip" style="background-color: white; color: black; padding: 10px; border-radius: 5px; border: 1px solid #ccc;">
          <strong>${stringLabel}</strong>:
      `;

      // NOSONAR - Optional chaining not supported in current environment
      if (tooltipConfig && tooltipConfig[seriesIndex] && dataPoints.length) {
        const data = dataPoints[dataPointIndex] || {};
        const template = tooltipConfig[seriesIndex].template;
        tooltipContent += buildTooltipContentByTemplate(template, color, value, data);
      } else {
        const legendName = w.config.series[seriesIndex].name;
        tooltipContent += `
          <div style="display: flex; align-items: center; margin: 5px 0;">
            <div style="width: 10px; height: 10px; background-color: ${color}; margin-right: 5px;"></div>
            <strong>${legendName}:</strong> ${value}
          </div>`;
      }

      tooltipContent += "</div>";
      return tooltipContent;
    }
  }
};
